import React, { useEffect, useLayoutEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"

import * as styles from "../styles"
import ColorPickerPopover, { AnchorPoint } from "./ColorPickerPopover"

// Each hex token we find in the gradient string. `start`/`end` are offsets
// into the original string, used to splice in a new colour without disturbing
// anything else (direction, stop positions, rgba() siblings, whitespace…).
type HexStop = {
  color: string
  start: number
  end: number
  position?: string
}

type GradientPickerProps = {
  gradientValue: string
  onChange: (next: string) => void
  onClose: () => void
  onStopSave: () => void
  onStopCancel: () => void
  anchorPoint: AnchorPoint
}

// Captures a hex token and, optionally, the % position that follows it. The
// surrounding gradient structure (function name, direction, whitespace) is
// left untouched — we never re-serialise the gradient string, only splice
// colour values in place.
const HEX_STOP_RE = /(#[0-9a-fA-F]{8}|#[0-9a-fA-F]{6}|#[0-9a-fA-F]{4}|#[0-9a-fA-F]{3})\b(?:\s+([\d.]+(?:%|px|em|rem)))?/g

const POPOVER_WIDTH = 104
const POPOVER_HEIGHT = 80 // 12+12 padding + 46 bar block + a little slack
const MARGIN = 8

export const parseStops = (gradient: string): HexStop[] => {
  const result: HexStop[] = []
  HEX_STOP_RE.lastIndex = 0
  let match: RegExpExecArray | null
  while ((match = HEX_STOP_RE.exec(gradient)) !== null) {
    result.push({
      color: match[1],
      start: match.index,
      end: match.index + match[1].length,
      position: match[2],
    })
  }
  return result
}

const replaceStopColor = (gradient: string, stop: HexStop, newColor: string): string =>
  gradient.slice(0, stop.start) + newColor + gradient.slice(stop.end)

// Where to render the swatch for stop i along the gradient bar (in %). Falls
// back to even distribution when a stop doesn't carry an explicit %.
const swatchPositionPercent = (index: number, total: number, stop: HexStop): number => {
  if (stop.position) {
    const match = stop.position.match(/^([\d.]+)%$/)
    if (match) return parseFloat(match[1])
  }
  return total <= 1 ? 0 : (index / (total - 1)) * 100
}

// Same anchor-near-the-click positioning as ColorPickerPopover — clamp to the
// viewport edges, never flip away from the click.
const positionFor = (point: AnchorPoint) => {
  const OFFSET = 12
  let left = point.x + OFFSET
  let top = point.y + OFFSET
  if (left + POPOVER_WIDTH > window.innerWidth - MARGIN) {
    left = point.x - POPOVER_WIDTH - OFFSET
  }
  if (top + POPOVER_HEIGHT > window.innerHeight - MARGIN) {
    top = window.innerHeight - POPOVER_HEIGHT - MARGIN
  }
  return { left: Math.max(MARGIN, left), top: Math.max(MARGIN, top) }
}

export const GradientPicker: React.FC<GradientPickerProps> = ({ gradientValue, onChange, onClose, onStopSave, onStopCancel, anchorPoint }) => {
  // We only ever splice hex colour values into the gradient string on edit —
  // direction, stop positions, whitespace, and any non-hex tokens round-trip
  // byte-for-byte. No local copy of the gradient is needed; the prop is the
  // source of truth and stops are derived from it on every render.
  const popoverRef = useRef<HTMLDivElement | null>(null)
  const [activeStop, setActiveStop] = useState<number | null>(null)
  const [stopAnchorPoint, setStopAnchorPoint] = useState<AnchorPoint | null>(null)
  const [position, setPosition] = useState(() => positionFor(anchorPoint))
  const stops = parseStops(gradientValue)

  useLayoutEffect(() => {
    setPosition(positionFor(anchorPoint))
  }, [anchorPoint])

  // Close on outside click / Escape. When a nested ColorPickerPopover is open
  // we no-op entirely: the user should be able to dismiss the colour picker
  // first with a click outside it, and then a second outside click closes
  // this gradient popover. (The colour picker has its own outside-click
  // handler that fires on the same event and closes itself.)
  useEffect(() => {
    const isColorPickerOpen = () => !!document.querySelector("[data-color-picker-popover]")
    const handleMouseDown = (event: MouseEvent) => {
      if (isColorPickerOpen()) return
      const target = event.target as Element
      if (popoverRef.current?.contains(target)) return
      onClose()
    }
    const handleKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return
      if (isColorPickerOpen()) return
      onClose()
    }
    document.addEventListener("mousedown", handleMouseDown)
    document.addEventListener("keydown", handleKey)
    return () => {
      document.removeEventListener("mousedown", handleMouseDown)
      document.removeEventListener("keydown", handleKey)
    }
  }, [onClose])

  const openStopColorPicker = (index: number, point: AnchorPoint) => {
    setActiveStop(index)
    setStopAnchorPoint(point)
  }

  const handleStopColorChange = (color: string) => {
    if (activeStop === null) return
    const stop = stops[activeStop]
    if (!stop) return
    onChange(replaceStopColor(gradientValue, stop, color))
  }

  const handleStopColorSave = () => {
    setActiveStop(null)
    setStopAnchorPoint(null)
    onStopSave()
  }

  const handleStopColorCancel = () => {
    setActiveStop(null)
    setStopAnchorPoint(null)
    onStopCancel()
  }

  const popover = (
    <styles.GradientPopover
      ref={popoverRef}
      style={{ ...position, width: POPOVER_WIDTH }}
      onMouseDown={(event) => event.stopPropagation()}
    >
      {stops.length === 0 ? (
        <div style={{ fontSize: 13, color: "#606e80" }}>
          This gradient string can&apos;t be parsed automatically.
        </div>
      ) : (
        <styles.GradientBarBlock>
          <styles.GradientBar style={{ background: gradientValue }} />
          {stops.map((stop, index) => {
            const positionPercent = swatchPositionPercent(index, stops.length, stop)
            // Keep swatches fully inside the bar's horizontal extent: at
            // positionPercent=0 the swatch's left edge sits at x=0; at 100
            // its right edge sits at x=bar_width; middle stops centred on
            // their %.
            return (
              <styles.GradientStopSwatch
                key={index}
                type="button"
                style={{
                  left: `${positionPercent}%`,
                  transform: `translateX(-${positionPercent}%)`,
                  background: stop.color,
                }}
                onClick={(event) => openStopColorPicker(index, { x: event.clientX, y: event.clientY })}
                title={`Edit colour ${index + 1}`}
              />
            )
          })}
        </styles.GradientBarBlock>
      )}

      {activeStop !== null && stopAnchorPoint && (
        <ColorPickerPopover
          colorValue={stops[activeStop]?.color ?? "#000000"}
          onChange={handleStopColorChange}
          onCancel={handleStopColorCancel}
          onSave={handleStopColorSave}
          anchorPoint={stopAnchorPoint}
        />
      )}
    </styles.GradientPopover>
  )

  return createPortal(popover, document.body)
}

export default GradientPicker
